import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SectionFeedbackBar } from "@/components/gooseberry-output/SectionFeedbackBar";

const CONFIDENCE_CLASSES = {
  high: "border-[#61CBA3] bg-[#61CBA3] text-white",
  medium: "border-[#61CBA3] bg-[rgba(97,203,163,0.22)] text-black",
  low: "border-black/20 bg-white text-black",
};

const NL_SECTION_TITLES = {
  context: "Context",
  goal: "Doel",
  "design phase": "Ontwerpfase",
  method: "Methode",
  "databases used": "Gebruikte databronnen",
  "synthetic users": "Synthetische gebruikers",
  assumptions: "Aannames",
  feedback: "Feedback",
  "interview questions": "Interviewvragen",
  "next methods": "Volgende methoden",
  insights: "Inzichten",
  limitations: "Beperkingen",
  "overall confidence": "Algemeen vertrouwen",
  "evidence & assumption check": "Bewijs- en aannamescheck",
  dialogue: "Dialoog",
  "insights from dialogue": "Inzichten uit dialoog",
};

function confidenceClasses(level) {
  const lvl = (level || "").toLowerCase();
  return CONFIDENCE_CLASSES[lvl] || CONFIDENCE_CLASSES.low;
}

export function ConfidenceChip({ level, language = "en" }) {
  const nl = language === "nl";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border ${confidenceClasses(level)}`}
      title={nl ? "Door het model aangegeven vertrouwen" : "Model-reported confidence"}
    >
      {nl ? "Vertrouwen" : "Confidence"} - {capFirst(level)}
    </span>
  );
}

export function SectionCard({ section, feedbackContext, language = "en" }) {
  const displayTitle = language === "nl" ? (NL_SECTION_TITLES[section.key] || titleCase(section.title)) : titleCase(section.title);
  return (
    <article className="rounded-2xl border border-black/10 bg-white p-5" data-testid={`synberry-output-section-${section.key.replace(/\s+/g, "-")}`}>
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display text-base font-semibold text-black">{displayTitle}</h3>
        {section.confidence && <ConfidenceChip level={section.confidence} language={language} />}
      </header>
      <div className="mt-3 prose-markdown text-black">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
            ul: ({ node, ...props }) => <ul {...props} className="mt-2 space-y-1.5 pl-5 list-disc marker:text-[#61CBA3]" />,
            li: ({ node, ...props }) => <li {...props} className="pl-1 text-sm text-black/80 leading-relaxed [&>p]:my-0" />,
            ol: ({ node, ...props }) => <ol {...props} className="mt-2 space-y-1.5 pl-5 list-decimal marker:text-[#61CBA3]" />,
            p: ({ node, ...props }) => <p {...props} className="text-sm text-black/80 leading-relaxed" />,
            strong: ({ node, ...props }) => <strong {...props} className="font-semibold text-black" />,
            table: ({ node, ...props }) => (
              <div className="my-3 w-full overflow-x-auto rounded-xl border border-black/10">
                <table {...props} className="min-w-[760px] w-full border-collapse text-left text-xs" />
              </div>
            ),
            thead: ({ node, ...props }) => <thead {...props} className="bg-[rgba(97,203,163,0.12)] text-black" />,
            tr: ({ node, ...props }) => <tr {...props} className="border-b border-black/10 last:border-b-0" />,
            th: ({ node, ...props }) => <th {...props} className="px-3 py-2.5 align-top font-semibold" />,
            td: ({ node, ...props }) => <td {...props} className="max-w-[240px] px-3 py-2.5 align-top text-black/75 whitespace-normal" />,
          }}
        >
          {section.body}
        </ReactMarkdown>
      </div>
      {feedbackContext?.runId && (
        <SectionFeedbackBar
          runId={feedbackContext.runId}
          sectionKey={section.key}
          sectionTitle={displayTitle}
          projectName={feedbackContext.projectName}
          mode={feedbackContext.mode}
          existing={feedbackContext.getFeedbackFor?.(section.key) || null}
          onSubmitted={feedbackContext.onSubmitted}
          language={language}
        />
      )}
    </article>
  );
}

export function SectionStack({ sectionKeys, parsed, testid, feedbackContext, language = "en" }) {
  const items = sectionKeys.map((k) => parsed.bySection[k]).filter(Boolean);
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-black/15 bg-white p-6 text-sm text-black/60" data-testid={testid}>
        {language === "nl" ? "Deze sectie is leeg voor deze run." : "This section is empty for this run."}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((s) => (
        <SectionCard key={s.title} section={s} feedbackContext={feedbackContext} language={language} />
      ))}
    </div>
  );
}

export function titleCase(s) {
  if (!s) return s;
  return s
    .split(" ")
    .map((w) => (w.length <= 3 ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(" ");
}

export function capFirst(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
}
